'use client';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { useEffect, useRef, useState } from 'react';

import { Badge } from '@/components/ui/badge';
import { SignUpButton } from '@clerk/nextjs';
import { AnimatePresence, motion } from 'framer-motion';
import { ChevronRight, CreditCard, HeadphonesIcon, VerifiedIcon } from 'lucide-react';
import Image from 'next/image';

import { PropertyListing } from '@/app/actions/serverActions';
import FaqSection from '@/components/elements/HostOnKawkab/FaqSection';
import PropertyCard from '../Properties/PropertyCard/PropertyCard';

export default function GuestsLanding({
  featuredProperties,
}: {
  featuredProperties: PropertyListing[];
}) {
  const [currentImageIndex, setCurrentImageIndex] = useState(0);

  const heroImages = ['/hero.jpeg'];

  const propertiesSection = useRef<HTMLDivElement | null>(null);

  const scrollToProperties = () => {
    if (propertiesSection === null) return;

    propertiesSection?.current?.scrollIntoView({
      behavior: 'smooth',
    });
  };

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentImageIndex((prevIndex) => (prevIndex + 1) % heroImages.length);
    }, 15000);

    return () => clearInterval(timer);
  }, []);

  return (
    <div className="flex min-h-screen flex-col">
      {/* Hero Section */}
      <section className="relative flex min-h-screen items-center justify-center overflow-hidden">
        <AnimatePresence>
          <motion.div
            key={currentImageIndex}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 1 }}
            className="absolute inset-0 z-0 -top-10"
          >
            <Image
              src={heroImages[currentImageIndex]}
              alt="Luxury Egyptian getaway"
              fill
              className="object-cover brightness-[0.30]"
              priority
            />
          </motion.div>
        </AnimatePresence>
        <div className="container relative z-10 mx-auto px-4 text-center text-white">
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5, duration: 0.8 }}
            className="mb-6 text-4xl font-bold leading-tight tracking-tighter sm:text-7xl md:text-7xl lg:text-7xl uppercease"
          >
            <span>
              A New Era of <span className="block md:hidden"> </span>Luxury Stays
            </span>
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.7, duration: 0.8 }}
            className="mx-auto mb-8 max-w-2xl text-xl text-gray-200"
          >
            Kawkb brings you the best of Egypt's gated communities stays, with verified guests and
            hosts, and secure e-payments in EGP.
          </motion.p>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.9, duration: 0.8 }}
            className="flex flex-col gap-4 sm:flex-row sm:justify-center"
          >
            <SignUpButton forceRedirectUrl={'/'}>
              <Button variant={'outline'} className="group  text-black bg-yellow-600 border-none">
                Join Our Community
                <ChevronRight className="ml-2 h-4 w-4 transition-transform group-hover:translate-x-1" />
              </Button>
            </SignUpButton>
            <Button
              variant={'outline'}
              className={'text-black'}
              onClick={() => scrollToProperties()}
            >
              Explore Stays
            </Button>
          </motion.div>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 1.1, duration: 0.8 }}
            className="mt-12 flex flex-wrap flex-col  justify-center gap-8 text-yellow-600"
          >
            <div className="cursor-pointer">
              <SignUpButton forceRedirectUrl={'/'}>
                <Badge className="items-center gap-2 py-2 px-4" variant={'secondary'}>
                  <span className="text-lg">Limited Spots Available</span>
                </Badge>
              </SignUpButton>
            </div>
          </motion.div>
        </div>

        <div className="absolute bottom-0 left-0 right-0 h-32 bg-gradient-to-t from-black to-transparent"></div>
      </section>

      {/* Experience Section */}
      <section className=" text-white py-24 text-primary-foreground">
        <div className="container mx-auto text-center">
          <div className="flex flex-col items-center  bg-black rounded-lg p-8 text-center md:rounded-xl lg:p-16">
            <h3 className="mb-3 max-w-3xl text-3xl font-semibold md:mb-4 md:text-4xl lg:mb-6">
              The Kawkb Experience
            </h3>
            <p className="mb-8 max-w-3xl lg:text-lg">
              We are committed to providing you with a seamless and enjoyable experience, from
              booking to check-out.
            </p>
            <div className="grid gap-8 md:grid-cols-3">
              {[
                {
                  icon: VerifiedIcon,
                  title: 'Verified Community',
                  description: 'Trust is everything. Safety and security are our top priority.',
                },
                {
                  icon: CreditCard,
                  title: 'Tech Enabled',
                  description: 'No Brokers, No hassle, no surprises.',
                },
                {
                  icon: HeadphonesIcon,
                  title: 'Localized Support',
                  description: "We're here for you in Arabic or English—anytime you need us.",
                },
              ].map((feature) => (
                <Card
                  key={feature.title}
                  className="h-full border-none bg-black transition-transform hover:scale-105 group"
                >
                  <CardContent className="flex h-full flex-col items-center gap-4 p-6 text-center">
                    <feature.icon className="h-16 w-16 text-yellow-600 group-hover:text-yellow-600" />
                    <h3 className="text-xl md:text-2xl font-semibold text-white group-hover:text-yellow-600">
                      {feature.title}
                    </h3>
                    <p className="text-lg text-gray-300 group-hover:text-yellow-600">
                      {feature.description}
                    </p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </div>
      </section>
      <section className="max-w-screen-2xl mx-auto p-12">
        <div className="text-center mb-12" ref={propertiesSection}>
          <Badge variant="secondary" className="mb-4 py-2 px-4">
            Kawkb's Favourites
          </Badge>
          <h2 className="text-3xl md:text-4xl font-bold mb-4">Exclusive Stays </h2>

          <p className="text-gray-600 max-w-2xl mx-auto">
            We handpick these stays, so you can enjoy a stay with peace of mind.
          </p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2  lg:grid-cols-3 gap-4">
          {featuredProperties.map((property) => (
            <PropertyCard key={property.id} property={property} />
          ))}
        </div>
      </section>

      {/* FAQ Section */}
      <FaqSection />
      {/* CTA Section */}
      <section className=" text-white py-24 text-primary-foreground">
        <div className="container mx-auto text-center">
          <div className="flex flex-col items-center  bg-black rounded-lg p-8 text-center md:rounded-xl lg:p-16">
            <h3 className="mb-3 max-w-3xl text-2xl font-semibold md:mb-4 md:text-4xl lg:mb-6">
              Ready to Get Started?
            </h3>
            <p className="mb-8 max-w-3xl lg:text-lg">
              Join our community of guests and hosts to experience the best of Egypt's gated
              communities stays.
            </p>
            <div className="flex w-full flex-col justify-center gap-2 sm:flex-row">
              <Button variant="outline" className="w-full sm:w-auto" asChild>
                Learn More
              </Button>
              <SignUpButton forceRedirectUrl={'/'}>
                <Button size="lg" variant="secondary" className="text-lg group bg-yellow-600">
                  Secure Your Membership
                  <ChevronRight className="ml-2 h-4 w-4 transition-transform group-hover:translate-x-1" />
                </Button>
              </SignUpButton>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
